
import Vue from 'vue';
import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';
import _unionBy from 'lodash/unionBy';
import _range from 'lodash/range';
import { mapActions, mapState } from 'vuex';
import { analytics, EVENTS } from '@/services/analytics';
import {
	RunnitNodeRun,
	RunnitNodeDef,
	RunnitNodeRunResult,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	RUNNIT_NODE_RUN_STATE,
	RUNNIT_NODE_FIELD_TYPE,
	getRunnitNodeRunResultFilePath,
	getRunnitLayerDownloadFilePath,
	getRunnitNodeRunResultDownloadFilePath,
	RUNNIT_NODE_STATIC_FIELDS_KEY,
	THUMB_SIZE,
	RunnitNodeField,
	extractFieldsByFieldType,
	buildFileNameForRunnitNodeRunResultDownload,
	RUNNIT_NODE_DEF_TOOL_TYPE,
} from '@run-diffusion/shared';
import { SELECTED_IMAGE_MODE } from '@/views/Runnits/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import GlassButton from '@/components/base/GlassButton.vue';
import ImageInfo from '@/components/ImageGallery/ImageInfo.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import RunnitResult from './RunnitResult.vue';
import { db, FieldValue, storage } from '@/firebase';
import { ROUTER } from '@/router/constants';
import RunnitDialog from './base/RunnitDialog.vue';
import ListItem from '@/components/base/ListItem.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import { RunnitsCRUDMixin } from '@/mixins/RunnitsCRUDMixin';
import { RunnitRemixMixin } from '@/mixins/RunnitRemixMixin';
import { ResultFavoritesMixin } from '@/mixins/ResultFavoritesMixin';
import { isPartialLibraryNodeRun } from '@/utils/libraryApi';
import { LibraryRunHydrationMixin } from '@/mixins/LibraryRunHydrationMixin';

interface RunnitNodeRunResultWithNodeRun {
	nodeRun: RunnitNodeRun;
	nodeRunResult: RunnitNodeRunResult;
}

export default Vue.extend({
	name: 'RunnitImageInfoCarouselDialog',
	props: {
		value: { type: Boolean, default: false },
		persistent: { type: Boolean, default: false },
		nodeRun: { type: Object, default: null },
		nodeRuns: { type: Array, default: null },
		nodeRunResult: { type: Object, default: null },
		resultFilter: { type: Function, default: (result) => true }, // if true allow the result to be displayed
	},
	data () {
		return {
			RUNNIT_NODE_RUN_RESULT_TYPE,
			RUNNIT_NODE_RUN_STATE,
			// Observed dimensions
			dimensions: {
				width: 0,
				height: 0,
			},
			vuetify: this.$vuetify,
			touchStartX: 0,
			touchEndX: 0,

			deleteConfirmOpen: false,
			deletingImage: false,
			downloadingImage: false,
			updatingPublicStatus: false,
			shareDialogOpen: false,
			srcIsFetching: false,
			remixUrl: '',
			// Firestore-hydrated runs for REST-sourced (partial) runs opened in the
			// carousel, keyed by run id. Stores the already-merged run rather than the
			// raw doc so the object identity is stable once hydrated — re-merging per
			// access would hand ImageInfoFields a new object every time and refire its
			// non-deep nodeRun watcher (which re-fetches the nodeDef). A `null` entry
			// means "in flight, or fetched and nothing to add", so its presence alone
			// dedupes concurrent calls and prevents refetching a missing run.
		};
	},
	mixins: [
		LibraryRunHydrationMixin,
		RunnitBulkActionsMixin,
		RunnitsCRUDMixin,
		RunnitRemixMixin,
		ResultFavoritesMixin,
	],
	created () {
		// Add event listeners for keydown when the component is created
		document.addEventListener('keydown', this.handleKeyDown);
		// Add event listeners for touch events
		document.addEventListener('touchstart', this.handleTouchStart);
		document.addEventListener('touchend', this.handleTouchEnd);
	},
	destroyed () {
		// Remove event listeners for keydown when the component is destroyed
		document.removeEventListener('keydown', this.handleKeyDown);
		// Remove event listeners for touch events
		document.removeEventListener('touchstart', this.handleTouchStart);
		document.removeEventListener('touchend', this.handleTouchEnd);
	},
	watch: {
		nodeRun: {
			immediate: true,
			async handler (newVal) {
				if (!newVal) return;
				this.hydratePartialNodeRun(newVal);
				await this.refreshComparisonUrls();
			},
		},
		value (isOpen: boolean) {
			if (isOpen && this.nodeRun) {
				this.hydratePartialNodeRun(this.nodeRun);
			}
		},
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'team',
			'teamUsers',
		]),
		displayNodeRun () {
			return this.hydratedRunFor(this.nodeRun);
		},
		// The Firestore-backed run to WRITE against. Deliberately not displayNodeRun:
		// that is a render merge (`{ ...hydrated, ...restPartial }`), so its `results`
		// are still the REST array — soft-deleted entries filtered out and a nodeRunId
		// injected into each. Writing that back to runnitNodeRuns would permanently
		// drop the deleted results and persist a field the doc never had.
		// Null while a library partial is unhydrated, which is the signal to refuse
		// the write rather than corrupt the document.
		writableNodeRun (): RunnitNodeRun | null {
			if (!this.nodeRun) return null;
			if (!isPartialLibraryNodeRun(this.nodeRun)) return this.nodeRun;
			return (this.nodeRun.id && this.hydratedNodeRunsById[this.nodeRun.id]) || null;
		},
		isPublic () {
			return this._get(this.nodeRunResult, 'isPublic');
		},
		isCurrentFavorited (): boolean {
			return this.isResultFavorited(this.nodeRunResult);
		},
		computedMaxWidth () {
			let maxWidth: string = '90%';

			if (this.$vuetify.breakpoint.mdAndUp) {
				maxWidth = '1700px';
			}

			return maxWidth;
		},
		resultsWithNodeRun () {
			let results: RunnitNodeRunResultWithNodeRun[] = [];
			if (this.runnitState.selectedImageMode === SELECTED_IMAGE_MODE.TILED) {
				results = this.nodeRuns.reduce((list: RunnitNodeRunResultWithNodeRun[], nodeRun: RunnitNodeRun) => {
					const length: number = this._get(nodeRun, `staticInputs[${RUNNIT_NODE_STATIC_FIELDS_KEY.numResults}]`) || 1;
					const nodeRunResults = nodeRun.results?.length ? nodeRun.results : _range(length).map(i => ({
						uuid: `placeholder-${nodeRun.id}-${i}`,
						nodeRunId: nodeRun.id,
						isNsfw: true,
						// Add a fixed height for placeholders to ensure consistent row sizing
						// This is used to calculate the aspect ratio. THUMB_SIZE x THUMB_SIZE is the thumbnail size
						file: {
							width: THUMB_SIZE,
							height: THUMB_SIZE
						}
					})) as RunnitNodeRunResult[];
					return _unionBy(list, nodeRunResults.filter(this.resultFilter).map((result) => ({ nodeRun, nodeRunResult: result })), 'nodeRunResult.uuid');
				}, []);
			} else if (this.runnitState.selectedImageMode === SELECTED_IMAGE_MODE.INFO) {
				results = this._get(this.nodeRun, 'results', []).filter(this.resultFilter).map((result) => ({ nodeRun: this.nodeRun, nodeRunResult: result }));
			}

			return results;
		},
		currNodeRunResultIndex () {
			if (!this.resultsWithNodeRun.length) return null;
			let foundIndex: number = null;
			this.resultsWithNodeRun.some(({ nodeRunResult }, index: number) => {
				if (nodeRunResult.uuid === this._get(this.nodeRunResult, 'uuid')) {
					foundIndex = index;
					return true;
				}
				return false;
			});
			return foundIndex;
		},
		resultIndexInNodeRun () {
			return (this.nodeRun?.results || []).findIndex((result) => result.uuid === this.nodeRunResult.uuid);
		},
		canBeFeatured (): boolean {
			const run = this.displayNodeRun;
			if (!run || isPartialLibraryNodeRun(run) || !run.nodeDef) return false;

			// Check if it's a company tool
			if (run.nodeDef.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY) return false;

			// Check if any model was used
			const modelFields: RunnitNodeField[] = extractFieldsByFieldType(run.nodeDef.fields, RUNNIT_NODE_FIELD_TYPE.MODEL_SINGLE_SELECT);
			if (!modelFields.length) return true;
			const model = (run.inputs || {})[modelFields[0].fieldDefUuid];
			return !model;
		},
		isComparisonActive (): boolean {
			return !!(this.runnitState && this.runnitState.imageComparisonMode);
		},
		isAsset3DResult (): boolean {
			return this._get(this.nodeRunResult, 'type') === RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D;
		},
	},
	methods: {
		// `updateRunnitState` arrives via the mixins; this one has no other source,
		// so without mapping it `setOpen` threw on close and never reached the
		// comparison reset below it.
		...mapActions([
			'incrementImageZoomResetTrigger',
		]),
		// Merge a run against its cached Firestore doc when we have one, so callers
		// get the hydrated inputs/fields/email/staticInputs + resolved nodeDef. The
		// live run is spread last, so its fresh values win (e.g. a post-toggle
		// re-emit). Used by displayNodeRun and by carousel stepping, so a step lands
		// on hydrated comparison data instead of the partial run's empty {}.
		// LibraryRunHydrationMixin calls this once a run finishes hydrating, so the
		// comparison URLs are recomputed against the full run rather than the REST
		// partial (preCalculateComparisonUrls returns {} without fields/inputs).
		async onNodeRunHydrated (): Promise<void> {
			await this.refreshComparisonUrls();
		},
		async refreshComparisonUrls () {
			if (!this.value || !this.displayNodeRun) return;
			const comparisonUrls = await this.preCalculateComparisonUrls(this.displayNodeRun);
			this.updateRunnitState({
				comparisonFieldSourceImageUrls: comparisonUrls,
			});
		},
		setOpen (val: boolean) {
			if (this.value !== val) {
				this.$emit('input', !!val);
			}

			if (!val) {
				// End the comparison session FIRST. It is the shared runnitState every
				// thumbnail on the board reads, so leaving it set makes every result
				// render a comparison slider (and swallow its click) until a reload.
				// Anything that can throw belongs after it, not before.
				this.updateRunnitState({
					imageComparisonMode: false,
					comparisonFieldDefUuid: null,
				});
				this.incrementImageZoomResetTrigger();
			}
		},
		onCancel () {
			this.setOpen(false);
		},
		onSelectionChosen () {
			this.onCancel();
		},
		hasPrevious () {
			return !(this.currNodeRunResultIndex <= 0)
		},
		async onPrevClick () {
			if (!this.hasPrevious()) return;

			const nextResultWithNodeRun = this.resultsWithNodeRun[this.currNodeRunResultIndex - 1];

			// Pre-populate comparison URLs for the new nodeRun and clear comparison mode.
			// Hydrate the target run first — resultsWithNodeRun carries the raw (partial)
			// run, whose empty fields/inputs would otherwise blank the comparison URLs.
			const newComparisonUrls = await this.preCalculateComparisonUrls(this.hydratedRunFor(nextResultWithNodeRun.nodeRun));
			this.updateRunnitState({
				imageComparisonMode: false,
				comparisonFieldDefUuid: null,
				comparisonFieldSourceImageUrls: newComparisonUrls,
			});

			this.$emit('on-prev-or-next-result', nextResultWithNodeRun);
		},
		hasNext () {
			return !(this.currNodeRunResultIndex >= (this.resultsWithNodeRun.length - 1));
		},
		async onNextClick () {
			if (!this.hasNext()) return;

			const nextResultWithNodeRun = this.resultsWithNodeRun[this.currNodeRunResultIndex + 1];

			// Pre-populate comparison URLs for the new nodeRun and clear comparison mode.
			// Hydrate the target run first — resultsWithNodeRun carries the raw (partial)
			// run, whose empty fields/inputs would otherwise blank the comparison URLs.
			const newComparisonUrls = await this.preCalculateComparisonUrls(this.hydratedRunFor(nextResultWithNodeRun.nodeRun));
			this.updateRunnitState({
				imageComparisonMode: false,
				comparisonFieldDefUuid: null,
				comparisonFieldSourceImageUrls: newComparisonUrls,
			});

			this.$emit('on-prev-or-next-result', nextResultWithNodeRun);
		},
		handleKeyDown (event) {
			if (!this.value) return;

			switch (event.key) {
				case 'ArrowLeft':
					this.handleLeftArrow();
					break;
				case 'ArrowRight':
					this.handleRightArrow();
					break;
			}
		},
		handleLeftArrow () {
			this.onPrevClick();
		},
		handleRightArrow () {
			this.onNextClick();
		},
		handleTouchStart (event: TouchEvent) {
			this.touchStartX = event.changedTouches[0].screenX;
		},
		handleTouchEnd (event: TouchEvent) {
			this.touchEndX = event.changedTouches[0].screenX;
			this.handleSwipe();
		},
		handleSwipe () {
			const swipeThreshold = 50; // Minimum distance (in pixels) to trigger a swipe
			const swipeDistance = this.touchEndX - this.touchStartX;

			if (Math.abs(swipeDistance) > swipeThreshold) {
				if (swipeDistance > 0) {
					this.handleRightSwipe();
				} else {
					this.handleLeftSwipe();
				}
			}
		},
		handleLeftSwipe () {
			this.onNextClick();
		},
		handleRightSwipe () {
			this.onPrevClick();
		},
		async preCalculateComparisonUrls (nodeRun) {
			if (!nodeRun || !nodeRun.fields || !nodeRun.inputs) {
				return {};
			}

			const comparisonUrls = {};

			// Find all image fields that have data
			const imageFields = [
				...extractFieldsByFieldType(nodeRun.fields, RUNNIT_NODE_FIELD_TYPE.IMG),
				...extractFieldsByFieldType(nodeRun.fields, RUNNIT_NODE_FIELD_TYPE.IMGS),
			].filter(field => nodeRun.inputs[field.fieldDefUuid]);

			// Fetch URLs for each image field
			for (const field of imageFields) {
				try {
					const inputData = nodeRun.inputs[field.fieldDefUuid];
					let nodeRunResult = null;
					let upload = null;

					// Handle different input data structures
					if (inputData.nodeRunResult) {
						nodeRunResult = inputData.nodeRunResult;
					} else if (inputData.upload) {
						upload = inputData.upload;
					} else if (inputData.image) {
						if (inputData.image.nodeRunResult) {
							nodeRunResult = inputData.image.nodeRunResult;
						} else if (inputData.image.upload) {
							upload = inputData.image.upload;
						}
					} else if (inputData.nodeRunResults && inputData.nodeRunResults.length > 0) {
						nodeRunResult = inputData.nodeRunResults[0];
					} else if (inputData.uploads && inputData.uploads.length > 0) {
						upload = inputData.uploads[0];
					}

					// Fetch the image URL if we have data
					if (nodeRunResult || upload) {
						const sourceImageUrl = await this.fetchImageUrl(nodeRunResult, upload);
						if (sourceImageUrl) {
							comparisonUrls[field.fieldDefUuid] = sourceImageUrl;
						}
					}
				} catch (error) {
					console.error('Error fetching comparison URL for field:', field.fieldDefUuid, error);
				}
			}

			return comparisonUrls;
		},
		async handleDownload () {
			try {
				this.downloadingImage = true;
				const { imageUrl, fileName } = await this.fetchSrc(this.nodeRunResult, 'nodeRunResult');
				if (!imageUrl || !fileName) {
					throw new Error('No image url or file name');
				}
				const builtFileName: string = buildFileNameForRunnitNodeRunResultDownload(fileName, this.nodeRunResult, this.nodeRun);
				await this.downloadImageByUrl(imageUrl, builtFileName);
				analytics.track(EVENTS.IMAGE_DOWNLOADED, {
					result_type: this._get(this.nodeRunResult, 'type', null),
					node_run_id: this._get(this.nodeRun, 'id', null),
					node_def_id: this._get(this.nodeRun, 'nodeDefId', null),
					bulk: false,
				});
			} catch (err) {
				console.error('Error downloading image', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Issue downloading image`,
					show: true,
					timeout: 30000,
				});
			} finally {
				this.downloadingImage = false;
			}
		},
		setDeleteImageDialog (isOpen) {
			this.deleteConfirmOpen = isOpen;
			if (!isOpen) {
				this.$refs.doubleConfirmDangerDialog.reset();
			}
		},
		async handleDelete () {
			this.deletingImage = true;
			try {
				const { success, count, reason } = await this.deleteNodeRunResultBatch([this.nodeRunResult], this.nodeRun.id);
				if (!success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Issues deleting image: ${reason}`,
						show: true,
						timeout: 30000,
					});
				} else {
					this.updateRunnitState({
						deletedImages: {
							...this.runnitState.deletedImages,
							nodeRunResults: [...this.runnitState.deletedImages.nodeRunResults, this.nodeRunResult],
						}
					});

					if (this.hasNext()) {
						this.onNextClick();
					} else if (this.hasPrevious()) {
						this.onPrevClick();
					} else {
						this.onCancel();
					}
				}
			} catch (err) {
				console.error('Error deleting image', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Issues deleting image`,
					show: true,
					timeout: 30000,
				});
			} finally {
				this.deletingImage = false;
				this.setDeleteImageDialog(false);
			}
		},
		async handlePublicToggle (setAsPublic: boolean) {
			if (this.nodeRunResult?.featured || this.nodeRunResult?.isFeaturedNodeDef) return;
			// Never write a REST partial's results back to the run doc — see
			// writableNodeRun. A partial that has not finished hydrating has no
			// authoritative results to rewrite, so refuse rather than corrupt it.
			const writableRun: RunnitNodeRun | null = this.writableNodeRun;
			if (!writableRun) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Still loading this generation, please try again in a moment',
					show: true,
					timeout: 5000,
				});
				return;
			}
			const sourceResults: RunnitNodeRunResult[] = writableRun.results || [];
			try {
				this.updatingPublicStatus = true;
				await db.runTransaction(async (transaction) => {
					if (this._get(this.nodeRunResult, `isPublic`) && !setAsPublic) { // is currently public
						const noOtherResultsArePublic = !sourceResults.find((result: RunnitNodeRunResult) => result.uuid !== this.nodeRunResult.uuid && result.isPublic);
						const updatedResults = sourceResults.map((result: RunnitNodeRunResult) => {
							if (result.uuid === this.nodeRunResult.uuid) {
								return _omit(result, ['isPublic']);
							}
							return result;
						})
						transaction.update(
							db.doc(`runnitNodeRuns/${writableRun.id}`),
							{
								...(noOtherResultsArePublic && { isPublic: FieldValue.delete() }),
								results: updatedResults
							},
						);
						// update local nodeRun state
						if (noOtherResultsArePublic) {
							delete this.nodeRunResult.isPublic;
						}
						this.applyLocalResultsUpdate(writableRun, updatedResults);
					} else if (setAsPublic) { // not currently public
						const updatedResults = sourceResults.map((result: RunnitNodeRunResult) => {
							if (result.uuid === this.nodeRunResult.uuid) {
								return { ...result, isPublic: true };
							}
							return result;
						});
						transaction.update(
							db.doc(`runnitNodeRuns/${writableRun.id}`),
							{
								isPublic: true,
								results: updatedResults
							}
						);
						// update local nodeRun state
						this.nodeRunResult.isPublic = true;
						this.applyLocalResultsUpdate(writableRun, updatedResults);
					}
				});
			} catch (error) {
				console.error('Error changing public state', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to update sharing status',
					show: true,
					timeout: 5000,
				});
			} finally {
				this.updatingPublicStatus = false;
				this.$emit('is-public-updated', { nodeRunResult: this.nodeRunResult, nodeRun: this.nodeRun });
			}
		},
		setShareDialogOpen (open: boolean) {
			this.shareDialogOpen = open;
			if (open && this.user && this.user.isAdmin) {
				this.updateRemixUrl();
			}
		},
		// Keep whichever object the view actually reads in sync after a write: the
		// hydrated Firestore doc for a library partial (the REST prop stays the
		// render source and is left alone), the prop itself otherwise.
		applyLocalResultsUpdate (writableRun: RunnitNodeRun, updatedResults: RunnitNodeRunResult[]) {
			if (writableRun.id && this.hydratedNodeRunsById[writableRun.id]) {
				this.$set(this.hydratedNodeRunsById[writableRun.id], 'results', updatedResults);
			} else {
				this.nodeRun.results = updatedResults;
			}
		},
		async handleCarouselFavoriteClick () {
			// displayNodeRun, not the raw prop: a REST partial has no nodeId/runnitId,
			// and writing those as undefined makes the favorite doc write fail.
			const nodeRun = this.displayNodeRun;
			if (!nodeRun || !this.nodeRunResult) return;
			await this.toggleResultFavorite(nodeRun, this.nodeRunResult);
		},
		async updateRemixUrl () {
			this.remixUrl = await this.getRemixUrl();
		},
		getShareUrl (source?: string) {
			const remixUrl = this.$router.resolve({
				name: ROUTER.RUNNIT_REMIX,
				query: {
					rnrid: this.nodeRun.id,
					ruuid: this.nodeRunResult.uuid,
					...(source && {
						utm_source: source, // Identifies the traffic source (e.g. google, facebook, newsletter)
						utm_medium: 'remix-link', // Identifies the medium (e.g. email, banner, social media)
						utm_campaign: this.user.uuId, // Identifies the campaign name
						utm_content: `${this.nodeRun.id}-${this.nodeRunResult.uuid}`, // Used to differentiate between multiple URLs
						// utm_term: this.nodeRunResult.type, // Used to differentiate between multiple keywords in paid search campaigns (e.g. "blue dress" vs "red dress")
					}),
				},
			}).href;
			return `${window.location.origin}${remixUrl}`;
		},
		async getRemixUrl () {
			const nodeRun = this.displayNodeRun;
			if (!nodeRun || isPartialLibraryNodeRun(nodeRun) || !nodeRun.nodeDef) {
				return '';
			}
			try {
				const inputValues = await this.fetchNodeRunValues(nodeRun, nodeRun.id);
				const encodedInputValues = encodeURIComponent(JSON.stringify(inputValues));
				const remixUrl = this.$router.resolve({
					name: ROUTER.RUNNIT_REROUTE,
					query: {
						toolId: nodeRun.nodeDef.id,
						inputValues: encodedInputValues,
					},
				}).href;
				return `${window.location.origin}${remixUrl}`;
			} catch (error) {
				console.error('Error generating remix URL:', error);
				return '';
			}
		},
		openPublicLink () {
			if (!this.nodeRunResult.isPublic) return;
			window.open(this.getShareUrl(), '_blank');
		},
		async copyPublicLink (source: string) {
			this.handlePublicToggle(true);
			if (!this.nodeRunResult.isPublic && !this.nodeRunResult.featured && !this.nodeRunResult.isFeaturedNodeDef) return;
			const shareUrl = this.getShareUrl(source);
			try {
				await navigator.clipboard.writeText(shareUrl);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Share link copied to clipboard!',
					show: true,
					timeout: 3000,
				});
			} catch (error) {
				console.error('Error copying to clipboard:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to copy share link',
					show: true,
					timeout: 5000,
				});
			}
		},
		async copyRemixUrl (source: string) {
			if (!this.remixUrl) {
				await this.updateRemixUrl();
			}
			if (!this.remixUrl) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to generate remix URL',
					show: true,
					timeout: 5000,
				});
				return;
			}
			try {
				await navigator.clipboard.writeText(this.remixUrl);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Remix URL copied to clipboard!',
					show: true,
					timeout: 3000,
				});
			} catch (error) {
				console.error('Error copying remix URL to clipboard:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to copy remix URL',
					show: true,
					timeout: 5000,
				});
			}
		},
		async shareToLinkedIn () {
			await this.handlePublicToggle(true);
			if (!this.nodeRunResult.isPublic && !this.nodeRunResult.featured && !this.nodeRunResult.isFeaturedNodeDef) return;
			const shareUrl = this.getShareUrl('linkedIn');
			const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
			analytics.track(EVENTS.IMAGE_SHARED, {
				share_channel: 'linkedin',
				node_run_id: this._get(this.nodeRun, 'id', null),
				node_def_id: this._get(this.nodeRun, 'nodeDefId', null),
			});
			window.open(url, '_blank');
		},
		async shareToX () {
			await this.handlePublicToggle(true);
			if (!this.nodeRunResult.isPublic && !this.nodeRunResult.featured && !this.nodeRunResult.isFeaturedNodeDef) return;
			const shareUrl = this.getShareUrl('x');
			const text = `Check out what I generated with RunDiffusion! You can view and remix it here:`;
			const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
			analytics.track(EVENTS.IMAGE_SHARED, {
				share_channel: 'x',
				node_run_id: this._get(this.nodeRun, 'id', null),
				node_def_id: this._get(this.nodeRun, 'nodeDefId', null),
			});
			window.open(url, '_blank');
		},
		// async shareToPinterest () {
		// 	await this.handlePublicToggle(true);
		// if (!this.nodeRunResult.isPublic && !this.nodeRunResult.featured && !this.nodeRunResult.isFeaturedNodeDef) return;
		// 	const shareUrl = this.getShareUrl('pinterest');
		// 	const text = `Check out what I generated with RunDiffusion! You can view and remix it here:`;

		// 	// Construct permanent URL
		// 	// const { filePath } = getRunnitNodeRunResultFilePath(this.nodeRunResult);
		// 	// const permanentUrl = `https://firebasestorage.googleapis.com/v0/b/image-ai-cb35d.appspot.com/o/${encodeURIComponent(filePath)}?alt=media`;
		// 	// const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(text)}&media=${encodeURIComponent(permanentUrl)}`;

		// 	const {imageUrl} = await this.fetchSrc(this.nodeRunResult);
		// 	const decodedImageUrl = decodeURIComponent(imageUrl);
		// 	const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(text)}&media=${encodeURIComponent(decodedImageUrl)}`;
		// 	window.open(url, '_blank');
		// },
		async shareToReddit () {
			await this.handlePublicToggle(true);
			if (!this.nodeRunResult.isPublic && !this.nodeRunResult.featured && !this.nodeRunResult.isFeaturedNodeDef) return;
			const shareUrl = this.getShareUrl('reddit');
			const text = `Check out what I generated with RunDiffusion! You can view and remix it here:`;
			const url = `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(text)}`;
			analytics.track(EVENTS.IMAGE_SHARED, {
				share_channel: 'reddit',
				node_run_id: this._get(this.nodeRun, 'id', null),
				node_def_id: this._get(this.nodeRun, 'nodeDefId', null),
			});
			window.open(url, '_blank');
		},
		async fetchSrc (input: RunnitNodeRunResult) {
			const nodeRunResult: RunnitNodeRunResult = input as RunnitNodeRunResult;

			let filePath: string, fileName: string, imageUrl: string;
			try {
				if (nodeRunResult.type === RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D && nodeRunResult.file) {
					const filePathData: { filePath: string } = getRunnitNodeRunResultDownloadFilePath(nodeRunResult);
					filePath = filePathData.filePath;
					fileName = nodeRunResult.file.name;
				} else if (nodeRunResult.type === RUNNIT_NODE_RUN_RESULT_TYPE.LAYERS && nodeRunResult.downloadFile) {
					const filePathData: { filePath: string } = getRunnitLayerDownloadFilePath(nodeRunResult);
					filePath = filePathData.filePath;
					fileName = nodeRunResult.downloadFile.name;
				} else {
					const filePathData: { filePath: string } = getRunnitNodeRunResultFilePath(nodeRunResult);
					filePath = filePathData.filePath;
					fileName = nodeRunResult.file.name;
				}

				if (!fileName || !filePath) {
					throw new Error('cannot find image');
				}

				const fileRef: any = storage.ref(filePath);
				imageUrl = await fileRef.getDownloadURL();
			} catch (err) {
				console.error('Error fetching image src', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error fetching image src`,
					show: true,
					timeout: 30000,
				});
				throw err;
			}
			return { fileName, imageUrl };
		},
		async fetchImageUrl (nodeRunResult: any, upload: any): Promise<string | null> {
			let filePath: string;

			if (nodeRunResult) {
				// Get file path for node run result
				const { getRunnitNodeRunResultFilePath } = await import('@run-diffusion/shared');
				const filePathData = getRunnitNodeRunResultFilePath(nodeRunResult);
				filePath = filePathData.filePath;
			} else if (upload) {
				// Get file path for upload
				const { getRunnitUploadFilePath } = await import('@run-diffusion/shared');
				const filePathData = getRunnitUploadFilePath(upload);
				filePath = filePathData.filePath;
			} else {
				return null;
			}

			// Fetch URL from Firebase Storage
			const fileRef = storage.ref(filePath);
			return await fileRef.getDownloadURL();
		},

	},
	components: {
		GlassButton,
		ImageInfo,
		RunnitResult,
		DoubleConfirmDangerDialog,
		RunnitDialog,
		ListItem,
		BaseToggle,
	},
});
